// Per-dialect DDL synthesizers for index ops. Companion to `dialect.ts`
// (column synthesizers). Kept in a sibling file so `dialect.ts` stays under
// the 200-line budget after the index applicator slice.
//
// PRD: Ralph/DdlStructuralEdit/PRD.md § Slice: Index Edit Applicator.
//   • CREATE INDEX (most engines) or ALTER TABLE … ADD INDEX (mysql ALTER
//     source path) per DSE3.
//   • DROP INDEX [ON <table>] (mssql + mysql require `ON <table>`) or
//     ALTER TABLE … DROP INDEX (mysql ALTER source path).
//   • R4: postgres CREATE INDEX CONCURRENTLY is preserved verbatim from the
//     source structure; v1 does not synthesize CONCURRENTLY from scratch.
//   • Cross-slice acceptance matrix: mssql `drop-index` requires the
//     `ON <table>` clause; missing table → documented runtime error.

import type { QualifiedName, SqlEngine } from '../../../sql-grammar/model';
import type { IndexColumn, IndexDef, IndexType } from '../../types';
import { DdlEditError } from '../../types';

import { maybeQuoteIdent } from './dialect';
import { type ExistingNameOrigin, nameExistingColumn } from './dialect.quoting';
import type { ColumnListOrigins } from './existingNameEvidence';

export interface IndexSynthOpts {
  /** WHERE the `{ kind: 'column' }` spelling at each POSITION of
   *  `index.columns` came from. An index names columns the catalog ALREADY
   *  HOLDS, so every element sits on the same three-way contract
   *  (`dialect.quoting.ts`'s {@link ExistingNameOrigin}) as a rename's old
   *  name, a drop's target and an ALTER COLUMN's subject. Asked PER ELEMENT
   *  because one list may legitimately hold a source-delimited `"Id"` next to
   *  a bare `target_col`. Defaults to `'bare-reference'`, the contract a
   *  caller holding only a name is under.
   *
   *  The INDEX of the entry in `index.columns` is what identifies it — the
   *  same list may hold `Id` and `"Id"`, two different columns on a folding
   *  engine that the stripped name cannot tell apart.
   *
   *  A STRING entry is asked too, when it is a column reference and nothing
   *  else. That does not widen what the string arm MEANS — an entry carrying
   *  index GRAMMAR (`a DESC`, `LOWER(email)`, MySQL `col(10)`, an opclass)
   *  still passes through byte-for-byte, because `existingNameEvidence.ts`'s
   *  `decodeTypedIndexColumnRef` (applied by `indexEdit.ts`) answers `null` for it and no name is ever
   *  resolved. What it buys is that a typed index list and a typed PRIMARY KEY
   *  list resolve one spelling the SAME way, instead of the index quietly
   *  handing a folding server the user's own bytes.
   *
   *  There is no STYLE half here: an index carries no host relation whose
   *  quoting it mirrors, so delimiters are only ever added when naming the
   *  column EXACTLY requires them. That is why a bare `target_col` keeps its
   *  bare spelling — the server's own fold is what resolves it. */
  readonly columnNameOrigin?: ColumnListOrigins;

  /** WHICH COLUMN each `index.columns` entry NAMES, when it names one at all.
   *  `undefined` at a position means the entry is not a column reference (an
   *  expression, or a string that is index grammar) and must be emitted
   *  verbatim. Supplied by the same resolver that answers
   *  {@link columnNameOrigin}, so the two cannot disagree about which
   *  positions are names. */
  readonly columnNameAt?: (index: number) => string | undefined;

  /** The origin question for `index.includeColumns`, asked SEPARATELY and by
   *  ITS OWN position. An INCLUDE list is columns only — no direction, no
   *  expression — so every entry is a name. It needs its own resolver for the
   *  reason the key synthesizers already do: PostgreSQL ACCEPTS an INCLUDE
   *  that overlaps its own key, so one shared resolver would hand the KEY
   *  element the INCLUDE element's delimiters, land the key on another column,
   *  and nothing downstream would catch it. */
  readonly includeColumnNameOrigin?: ColumnListOrigins;

  /** {@link columnNameAt} for the INCLUDE list. Every entry names a column, so
   *  this answers for every position; it exists so a typed `"Id"` there is
   *  decoded once, beside the key list's, rather than twice. */
  readonly includeColumnNameAt?: (index: number) => string | undefined;
}

/** The default origin resolver: every element is a reference the caller wrote
 *  bare, which is what a free-text column list is until the source statement
 *  says otherwise. */
const BARE_REFERENCE = (): ExistingNameOrigin => 'bare-reference';

/** Use the qualified name's verbatim raw text when present so the source's
 *  quoting / casing carries through. Mirrors `tableText` in dialect.ts —
 *  duplicated minimally (3 lines) to avoid widening dialect.ts's exports. */
function qnText(engine: SqlEngine, qn: QualifiedName): string {
  if (qn.raw && qn.raw.length > 0) return qn.raw;
  const name = maybeQuoteIdent(engine, qn.name, qn.quoted);
  if (qn.schema) {
    return `${maybeQuoteIdent(engine, qn.schema, qn.quoted)}.${name}`;
  }
  return name;
}

/** `CREATE [UNIQUE] INDEX [CONCURRENTLY] <name> ON <table> (<cols>)
 *  [INCLUDE (incCols)] [WHERE …]`.
 *  A string column entry is spliced into `(col1, col2, …)` without being
 *  re-PARSED, but it IS asked whether the whole string is a column reference:
 *  one that is (`col_a`, `"Col_a"`) is named against the source's own
 *  declaration, one that is a verbatim segment (`a DESC`, `LOWER(name)`) keeps
 *  its bytes. See {@link IndexSynthOpts.columnNameAt}.
 *
 *  v2 Slice C1 — filtered / partial indexes: `partialWhereText` emits the
 *  trailing `WHERE …` clause on every engine that actually supports it
 *  (mssql 2008+, postgres, sqlite). mysql + oracle do not support partial
 *  indexes; passing `partialWhereText` to either throws a `DdlEditError`
 *  (`code: 'engine-does-not-support-op'`) pointing at the v2 Slice C3
 *  functional-index workaround (the only portable equivalent: an index on
 *  a CASE expression that selects the same predicate).
 *
 *  v2 Slice C2 — included / covering columns: `includeColumns` emits the
 *  `INCLUDE (cols)` clause between the key column list and any partial-index
 *  WHERE clause. Only mssql (2005+) and postgres (11+) accept the clause;
 *  mysql / oracle / sqlite throw at apply time. There is no portable
 *  workaround for non-key columns on those engines — adding the requested
 *  columns to the key column list changes the index semantics (key order /
 *  uniqueness scope), so the synthesizer surfaces the unsupported request
 *  rather than silently degrading. */
export function synthesizeCreateIndex(
  engine: SqlEngine,
  index: IndexDef,
  opts: IndexSynthOpts = {},
): string {
  if (index.partialWhereText !== undefined && index.partialWhereText.length > 0) {
    rejectPartialIndexIfUnsupported(engine);
  }
  if (index.includeColumns !== undefined && index.includeColumns.length > 0) {
    rejectIncludeColumnsIfUnsupported(engine);
  }
  if (index.indexType !== undefined) {
    rejectIndexTypeIfUnsupported(engine, index.indexType);
  }
  // mssql FULLTEXT INDEX has special syntax — no top-level name (the
  // catalog identifies the index by table), KEY INDEX requirement, optional
  // catalog clause. Branch entirely rather than threading through the
  // regular template, which doesn't fit. (v2 Slice C4)
  if (engine === 'mssql' && index.indexType?.kind === 'fulltext') {
    return synthesizeMssqlFulltextIndex(index, index.indexType, opts);
  }
  const unique = index.unique ? 'UNIQUE ' : '';
  // CONCURRENTLY is only valid on Postgres; preserve the flag if it's set
  // (the extractor only sets it from a source CREATE INDEX CONCURRENTLY,
  // never out of thin air per R4).
  const concurrent = index.concurrent && engine === 'postgres' ? 'CONCURRENTLY ' : '';
  // Specialized index-type keyword between CREATE [UNIQUE] and INDEX —
  // mssql/mysql/oracle put the keyword here (e.g. FULLTEXT, SPATIAL,
  // BITMAP, COLUMNSTORE). pgsql uses USING after the table name instead.
  const typeBefore = index.indexType ? indexTypeKeywordBeforeIndex(engine, index.indexType) : '';
  const name = qnText(engine, index.name);
  const table = qnText(engine, index.table);
  // pgsql USING <method> goes between `ON <table>` and the column list.
  const typeAfterTable = index.indexType ? indexTypeAfterTable(engine, index.indexType) : '';
  // mssql clustered columnstore covers the entire row and omits the column
  // list (`CREATE CLUSTERED COLUMNSTORE INDEX name ON tbl;`). All other
  // type/engine combinations keep the regular `(cols)` suffix.
  const omitCols = index.indexType ? isClusteredColumnstore(engine, index.indexType) : false;
  const colsClause = omitCols ? '' : ` (${formatIndexColumns(engine, index.columns, opts)})`;
  // mysql USING BTREE/HASH and oracle INDEXTYPE IS … go after the column
  // list, before INCLUDE / WHERE.
  const typeAfterCols = index.indexType ? indexTypeAfterColumns(engine, index.indexType) : '';
  // `includeColumns` is `readonly string[]`, and it does NOT need widening to
  // `(string | IndexColumn)[]` to sit on the origin contract: the key
  // synthesizers have always named a `readonly string[]` INCLUDE list through
  // their own `includeColumnNameOrigin` (`dialect.primaryKey.ts`), and this is
  // that same shape. An INCLUDE list is columns only, so every entry names one.
  const include = index.includeColumns && index.includeColumns.length > 0
    ? ` INCLUDE (${formatIncludeColumns(engine, index.includeColumns, opts)})`
    : '';
  const where = index.partialWhereText ? ` WHERE ${index.partialWhereText}` : '';
  return `CREATE ${unique}${typeBefore}INDEX ${concurrent}${name} ON ${table}${typeAfterTable}${colsClause}${typeAfterCols}${include}${where}`;
}

/** Render an `IndexDef.columns` entry list per the engine's functional-index
 *  idiom (v2 Slice C3). A bare-string entry is NAMED when the caller resolved
 *  a name for it and passes through verbatim when it did not — see
 *  `formatIndexColumn`. Discriminated `{ kind: 'column', name }` entries
 *  always NAME an existing column. Discriminated
 *  `{ kind: 'expression', text }` entries:
 *    - mssql:  throw — direct expression indexes are not supported on Jam's
 *              mssql matrix; use the computed-column-then-index workaround.
 *    - mysql:  wrap in extra parens to disambiguate functional indexes from
 *              column references (mysql 8.0.13+ requires `((expr))`).
 *    - other:  emit `text` verbatim. */
function formatIndexColumns(
  engine: SqlEngine,
  columns: readonly (string | IndexColumn)[],
  opts: IndexSynthOpts,
): string {
  return columns.map((c, i) => formatIndexColumn(engine, c, i, opts)).join(', ');
}

/** Name a column the catalog ALREADY HOLDS, for an index. `styleDelimits` is
 *  `false` because an index carries no host relation whose quoting it mirrors:
 *  delimiters appear only when naming the column exactly requires them. */
function nameIndexColumn(engine: SqlEngine, name: string, origin: ExistingNameOrigin): string {
  const { text, delimit } = nameExistingColumn(name, engine, origin, false);
  return maybeQuoteIdent(engine, text, delimit);
}

/** The INCLUDE list, each entry named by its OWN position. An entry the caller
 *  resolved no name for keeps its bytes — the same posture the key list takes
 *  for grammar, though an INCLUDE list should never hold any. */
function formatIncludeColumns(
  engine: SqlEngine,
  columns: readonly string[],
  opts: IndexSynthOpts,
): string {
  return columns.map((c, i) => {
    const named = opts.includeColumnNameAt?.(i);
    if (named === undefined) return c;
    return nameIndexColumn(engine, named, (opts.includeColumnNameOrigin ?? BARE_REFERENCE)(i));
  }).join(', ');
}

function formatIndexColumn(
  engine: SqlEngine,
  entry: string | IndexColumn,
  index: number,
  opts: IndexSynthOpts,
): string {
  // A string entry that is index GRAMMAR (`a DESC`, `LOWER(email)`) passes
  // through byte-for-byte. Only the CALLER can tell that from a bare column
  // reference, and it says which by resolving a name for one and not the
  // other (`existingNameEvidence.ts`'s `decodeTypedIndexColumnRef`, applied by `indexEdit.ts`). A caller that
  // supplies no resolver keeps every string verbatim — the v1 contract,
  // unchanged.
  if (typeof entry === 'string') {
    const named = opts.columnNameAt?.(index);
    if (named === undefined) return entry;
    return nameIndexColumn(engine, named, (opts.columnNameOrigin ?? BARE_REFERENCE)(index));
  }
  // A `kind: 'column'` entry is a COLUMN NAME the catalog already holds, not
  // the string arm's raw source text — so it is named, not echoed. Emitting
  // the typed bytes bare is only right when they ARE bare: a source-delimited
  // `"Id"` sent out unquoted asks PostgreSQL for `id` and Oracle for `ID`, and
  // a name with no bare spelling at all (`user id`, a reserved word) is not
  // readable as an identifier without delimiters. Passing `styleDelimits:
  // false` keeps an ordinary bare `col_b` exactly as it is — see
  // `IndexSynthOpts.columnNameOrigin`.
  if (entry.kind === 'column') {
    return nameIndexColumn(engine, entry.name, (opts.columnNameOrigin ?? BARE_REFERENCE)(index));
  }
  rejectExpressionIndexIfUnsupported(engine);
  return engine === 'mysql' ? `(${entry.text})` : entry.text;
}

/** mysql `ALTER TABLE <table> ADD [UNIQUE] INDEX <name> (<cols>)`. Used when
 *  the source statement is an ALTER TABLE on mysql; CREATE INDEX is preferred
 *  on every other dialect (and on mysql with a non-ALTER source). mysql does
 *  not support partial indexes nor INCLUDE / covering columns — passing
 *  either throws (the error paths match `synthesizeCreateIndex` so callers
 *  see one message shape regardless of source statement kind). */
export function synthesizeAlterAddIndex(
  table: QualifiedName,
  index: IndexDef,
  opts: IndexSynthOpts = {},
): string {
  if (index.partialWhereText !== undefined && index.partialWhereText.length > 0) {
    rejectPartialIndexIfUnsupported('mysql');
  }
  if (index.includeColumns !== undefined && index.includeColumns.length > 0) {
    rejectIncludeColumnsIfUnsupported('mysql');
  }
  if (index.indexType !== undefined) {
    rejectIndexTypeIfUnsupported('mysql', index.indexType);
  }
  const unique = index.unique ? 'UNIQUE ' : '';
  // FULLTEXT / SPATIAL keyword goes between ADD [UNIQUE] and INDEX on the
  // mysql ALTER source path (matches the standalone CREATE shape). USING
  // BTREE/HASH is appended after the column list (v2 Slice C4).
  const typeBefore = index.indexType ? indexTypeKeywordBeforeIndex('mysql', index.indexType) : '';
  const tbl = qnText('mysql', table);
  const name = qnText('mysql', index.name);
  const cols = formatIndexColumns('mysql', index.columns, opts);
  const typeAfterCols = index.indexType ? indexTypeAfterColumns('mysql', index.indexType) : '';
  return `ALTER TABLE ${tbl} ADD ${unique}${typeBefore}INDEX ${name} (${cols})${typeAfterCols}`;
}

/** Throws when the engine does not support partial / filtered indexes. mysql
 *  and oracle are the only supported engines without partial-index syntax
 *  in their CREATE INDEX surface; the message points at v2 Slice C3's
 *  functional-index workaround (an index on a CASE expression). Other
 *  engines fall through silently. */
function rejectPartialIndexIfUnsupported(engine: SqlEngine): void {
  if (engine === 'mysql') {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: mysql does not support partial / filtered indexes — `
        + `mysql CREATE INDEX has no WHERE clause. Use the v2 Slice C3 `
        + `functional-index workaround: index a CASE expression that selects `
        + `rows matching the predicate (e.g. `
        + `\`CREATE INDEX … ((CASE WHEN <pred> THEN <key> END))\`).`,
    );
  }
  if (engine === 'oracle') {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: oracle does not support partial / filtered indexes — `
        + `oracle CREATE INDEX has no WHERE clause. Use the v2 Slice C3 `
        + `function-based-index workaround: index a CASE expression that `
        + `selects rows matching the predicate (e.g. `
        + `\`CREATE INDEX … (CASE WHEN <pred> THEN <key> END)\`); rows where `
        + `the CASE returns NULL are excluded from the index.`,
    );
  }
}

/** Throws when the engine does not support `INCLUDE` / covering columns on
 *  CREATE INDEX (v2 Slice C2). Only mssql (2005+) and postgres (11+) accept
 *  the clause. There is no portable workaround: moving the requested columns
 *  into the key column list changes the index semantics (sort order +
 *  uniqueness scope), so the synthesizer surfaces the unsupported request
 *  rather than silently degrading. */
function rejectIncludeColumnsIfUnsupported(engine: SqlEngine): void {
  if (engine === 'mssql' || engine === 'postgres') return;
  if (engine === 'mysql') {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: mysql does not support INCLUDE / covering columns on `
        + `CREATE INDEX — mysql's CREATE INDEX has no INCLUDE clause. There `
        + `is no portable workaround; moving the requested columns into the `
        + `key column list changes the index's sort order and uniqueness scope.`,
    );
  }
  if (engine === 'oracle') {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: oracle does not support INCLUDE / covering columns on `
        + `CREATE INDEX — oracle's CREATE INDEX has no INCLUDE clause. There `
        + `is no portable workaround; moving the requested columns into the `
        + `key column list changes the index's sort order and uniqueness scope.`,
    );
  }
  if (engine === 'sqlite') {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: sqlite does not support INCLUDE / covering columns on `
        + `CREATE INDEX — sqlite's CREATE INDEX has no INCLUDE clause. There `
        + `is no portable workaround; moving the requested columns into the `
        + `key column list changes the index's sort order and uniqueness scope.`,
    );
  }
}

/** Throws when the engine does not support direct expression / functional
 *  indexes on `CREATE INDEX` (v2 Slice C3). Only mssql is gated — mssql 2022
 *  introduced experimental support but Jam's supported-engine matrix does
 *  not gate on it; the documented workaround is to add a PERSISTED computed
 *  column (Slice A3) and index that. pgsql / mysql / oracle / sqlite all
 *  support direct expression indexes natively (mysql via the double-paren
 *  form; the synthesizer handles that wrapping in `formatIndexColumn`). */
function rejectExpressionIndexIfUnsupported(engine: SqlEngine): void {
  if (engine === 'mssql') {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: mssql does not support direct expression / functional `
        + `indexes — mssql 2022 has experimental support but Jam's `
        + `supported-engine matrix does not gate on it. Use the computed-`
        + `column-then-index workaround: add a PERSISTED computed column via `
        + `the v2 Slice A3 'add-column' op (column.generated.expr = '<expr>', `
        + `virtual: false), then index the computed column by name.`,
    );
  }
}

/** `DROP INDEX <name>` (postgres / oracle / sqlite) or `DROP INDEX <name> ON
 *  <table>` (mssql / mysql). Throws when mssql is given no `table` —
 *  cross-slice acceptance matrix says mssql `DROP INDEX` requires `ON
 *  <table>`, and the type lets the caller pass undefined; this is the runtime
 *  guard. */
export function synthesizeDropIndex(
  engine: SqlEngine,
  indexName: QualifiedName,
  table: QualifiedName | undefined,
): string {
  const name = qnText(engine, indexName);
  if (engine === 'mssql') {
    if (!table) {
      throw new DdlEditError(
        'invalid-edit',
        `applyDdlEdit: mssql DROP INDEX requires 'table' — pass it on the edit `
          + `or extract it from the source DROP INDEX (see CONSUMER_HANDOFF.md)`,
      );
    }
    return `DROP INDEX ${name} ON ${qnText(engine, table)}`;
  }
  if (engine === 'mysql' && table) {
    return `DROP INDEX ${name} ON ${qnText(engine, table)}`;
  }
  return `DROP INDEX ${name}`;
}

/** mysql `ALTER TABLE <table> DROP INDEX <name>`. Used on the mysql ALTER
 *  source path. */
export function synthesizeAlterDropIndex(table: QualifiedName, indexName: QualifiedName): string {
  const tbl = qnText('mysql', table);
  const name = qnText('mysql', indexName);
  return `ALTER TABLE ${tbl} DROP INDEX ${name}`;
}

// ─── v2 Slice C4 — Specialized index types ─────────────────────────────────

/** Per-engine support sets for `IndexType.kind`. The tables are the source of
 *  truth for `rejectIndexTypeIfUnsupported`. Engines absent from a kind's
 *  set throw with a documented message naming the supported kinds. */
const ENGINE_INDEX_TYPE_KINDS: Record<SqlEngine, readonly IndexType['kind'][]> = {
  mssql: ['btree', 'columnstore', 'xml', 'spatial', 'fulltext'],
  postgres: ['btree', 'hash', 'gin', 'gist', 'brin', 'spgist'],
  mysql: ['btree', 'hash', 'fulltext', 'spatial'],
  oracle: ['btree', 'bitmap', 'domain'],
  sqlite: ['btree'],
};

function rejectIndexTypeIfUnsupported(engine: SqlEngine, t: IndexType): void {
  const supported = ENGINE_INDEX_TYPE_KINDS[engine];
  if (!supported.includes(t.kind)) {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: ${engine} does not support index type '${t.kind}' — `
        + `${engine} supports: ${supported.join(', ')}.`,
    );
  }
}

/** Specialized index-type keyword that goes between `CREATE [UNIQUE]` and
 *  `INDEX`. mssql/mysql/oracle put the keyword here (e.g. `FULLTEXT`,
 *  `SPATIAL`, `BITMAP`, `[CLUSTERED|NONCLUSTERED] COLUMNSTORE`). pgsql uses
 *  `USING <method>` after the table name instead — see
 *  `indexTypeAfterTable`. Returns the empty string when no extra keyword is
 *  needed (default b-tree on every engine; pgsql's specialized methods). */
function indexTypeKeywordBeforeIndex(engine: SqlEngine, t: IndexType): string {
  if (engine === 'mssql') {
    if (t.kind === 'columnstore') {
      return t.clustered ? 'CLUSTERED COLUMNSTORE ' : 'NONCLUSTERED COLUMNSTORE ';
    }
    if (t.kind === 'xml') return 'XML ';
    if (t.kind === 'spatial') return 'SPATIAL ';
    // mssql FULLTEXT is handled by the dedicated synthesizer branch — this
    // function is never reached with kind: 'fulltext' on mssql.
  }
  if (engine === 'mysql') {
    if (t.kind === 'fulltext') return 'FULLTEXT ';
    if (t.kind === 'spatial') return 'SPATIAL ';
  }
  if (engine === 'oracle') {
    if (t.kind === 'bitmap') return 'BITMAP ';
  }
  return '';
}

/** pgsql `USING <method>` clause that goes between `ON <table>` and the
 *  column list. Returns the empty string for non-pgsql engines and for the
 *  default b-tree (pgsql omits `USING btree` because it is the implicit
 *  default — emitting it would still parse but is non-idiomatic). */
function indexTypeAfterTable(engine: SqlEngine, t: IndexType): string {
  if (engine !== 'postgres') return '';
  if (t.kind === 'btree') return '';
  return ` USING ${t.kind}`;
}

/** Trailing per-engine clause that goes after the column list — mysql
 *  `USING BTREE|HASH`, oracle `INDEXTYPE IS <indextype>`. Returns the empty
 *  string when no trailing clause is needed. */
function indexTypeAfterColumns(engine: SqlEngine, t: IndexType): string {
  if (engine === 'mysql' && (t.kind === 'btree' || t.kind === 'hash')) {
    return ` USING ${t.kind.toUpperCase()}`;
  }
  if (engine === 'oracle' && t.kind === 'domain') {
    return ` INDEXTYPE IS ${t.indextype}`;
  }
  return '';
}

/** mssql `CLUSTERED COLUMNSTORE INDEX` covers the entire row and rejects a
 *  column list at execute time — every column is part of the columnstore.
 *  The synthesizer omits `(cols)` so the emitted SQL parses on mssql. */
function isClusteredColumnstore(engine: SqlEngine, t: IndexType): boolean {
  return engine === 'mssql' && t.kind === 'columnstore' && t.clustered === true;
}

/** mssql FULLTEXT INDEX has dedicated syntax — there is no top-level index
 *  name (the catalog identifies the index by table), `KEY INDEX <pk>` is
 *  required to name the unique key index used to identify rows in the
 *  fulltext catalog, and `ON <catalog>` is optional. The `IndexDef.name`
 *  field is ignored on this path (consumers can pass any placeholder; the
 *  emitted SQL never references it).
 *
 *  Spec: `CREATE FULLTEXT INDEX ON <table> (<cols>) KEY INDEX <key>
 *  [ON <catalog>]`. The synthesizer throws when `mssqlKeyIndex` is
 *  missing — mssql rejects `CREATE FULLTEXT INDEX` without it at parse
 *  time, so surfacing the requirement upfront gives consumers a clearer
 *  error than the engine's. */
function synthesizeMssqlFulltextIndex(
  index: IndexDef,
  type: Extract<IndexType, { kind: 'fulltext' }>,
  opts: IndexSynthOpts,
): string {
  if (!type.mssqlKeyIndex || type.mssqlKeyIndex.length === 0) {
    throw new DdlEditError(
      'invalid-edit',
      `applyDdlEdit: mssql FULLTEXT INDEX requires 'mssqlKeyIndex' — mssql's `
        + `CREATE FULLTEXT INDEX syntax has a mandatory 'KEY INDEX <name>' `
        + `clause naming the unique key index used to identify rows in the `
        + `fulltext catalog. Pass the unique-index name on `
        + `'IndexType.mssqlKeyIndex'.`,
    );
  }
  const tbl = qnText('mssql', index.table);
  const cols = formatIndexColumns('mssql', index.columns, opts);
  const catalog = type.mssqlCatalog && type.mssqlCatalog.length > 0
    ? ` ON ${type.mssqlCatalog}`
    : '';
  return `CREATE FULLTEXT INDEX ON ${tbl} (${cols}) KEY INDEX ${type.mssqlKeyIndex}${catalog}`;
}
